import { r, s } from '@/theme/tokens';
import { type UiTone, uiToneSeverityMap } from '@/theme/uiTokens';
import { Text } from '@/ui/primitives';
import { Alert } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement } from 'react';

export type StatusBannerProps = {
  tone?: UiTone;
  message?: string;
  onClose?: () => void;
  children?: ReactElement | string;
  variant?: 'standard' | 'outlined' | 'filled';
  dense?: boolean;
  sx?: SxProps<Theme>;
};

export const StatusBanner = ({
  tone = 'blue',
  message,
  onClose,
  children,
  variant = 'outlined',
  dense = false,
  sx,
}: StatusBannerProps): ReactElement | null => {
  if (!message && !children) return null;
  const severity = uiToneSeverityMap[tone];
  const denseSx = dense ? { py: s('sm'), px: s('md'), alignItems: 'center' } : undefined;
  const alertSx = {
    borderRadius: r('card'),
    py: dense ? s('sm') : s('md'),
    px: dense ? s('md') : s('lg'),
    ...(denseSx ?? {}),
    '& .MuiAlert-message': {
      color: 'text.primary',
      paddingRight: s('xs'),
      paddingLeft: s('xs'),
      fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
      lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight,
    },
    '& .MuiAlert-icon': {
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'center',
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
      paddingRight: s('sm'),
      marginLeft: s('xs'),
      paddingLeft: 0,
    },
    '& .MuiAlert-action .MuiIconButton-root': {
      padding: s('xs'),
      transform: 'translateY(-1px)',
    },
    '& .MuiAlert-action .MuiButtonBase-root': {
      marginLeft: `-${s('xs')}`,
    },
  };
  const resolvedSx = sx
    ? ([alertSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>)
    : alertSx;

  const content =
    typeof message === 'string' ? (
      <Text
        role="body"
        component="span"
        sx={{
          fontSize: (theme) => theme.typography.subtitle1.fontSize,
          lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
        }}
      >
        {message}
      </Text>
    ) : (
      children
    );

  return (
    <Alert severity={severity} color={severity} variant={variant} onClose={onClose} sx={resolvedSx}>
      {content}
    </Alert>
  );
};
